/**
 * Utility functions for send-on-behalf permissions.
 *
 * This module consolidates the logic for determining whether a user should see
 * send-on-behalf UI (user selection dropdowns) across different content types
 * (blogs, social media, multi-campaign scheduler, etc.).
 */

export interface SendOnBehalfParams {
	/** True when CSM is impersonating an account (admin site) */
	isUsingAccountImpersonation: boolean;
	/** User's role from session */
	userRole: string;
	/** Account preference - can pass userSession.account.preferences.sendOnBehalfEnabled */
	sendOnBehalfEnabled?: boolean;
}

/**
 * Determines whether the send-on-behalf UI should be displayed.
 *
 * Rules:
 * 1. CSMs using account impersonation (admin site) ALWAYS have access
 * 2. Regular admins require BOTH admin role AND sendOnBehalfEnabled preference
 * 3. Non-admins never have access
 *
 * @returns true if send-on-behalf UI should be shown
 */
export function shouldShowSendOnBehalfUI(params: SendOnBehalfParams): boolean {
	const { isUsingAccountImpersonation, userRole, sendOnBehalfEnabled } = params;

	// CSMs with impersonation always have access, regardless of account settings
	if (isUsingAccountImpersonation) {
		return true;
	}

	// Regular users need admin role AND the preference enabled
	const isAdmin = isAdminRole(userRole);
	return isAdmin && Boolean(sendOnBehalfEnabled);
}

/**
 * Determines if a user has admin-level permissions based on role.
 */
export function isAdminRole(userRole: string): boolean {
	const normalizedRole = userRole?.toLowerCase();
	return normalizedRole === 'admin' || normalizedRole === 'superadmin';
}
